import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Eye, Edit, Trash2, X as XIcon, Send, Loader2, Search, ArrowRight, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ImageMarkup from "@/components/admin/ImageMarkup";
import NewQuoteDialog from "@/components/admin/NewQuoteDialog";
import api, { apiError } from "@/lib/api";

const quoteStatusColor = {
  pending: "bg-amber-500/15 text-amber-600",
  replied: "bg-blue-500/15 text-blue-600",
  accepted: "bg-emerald-500/15 text-emerald-600",
  declined: "bg-red-500/15 text-red-600",
  cancelled: "bg-zinc-500/15 text-zinc-600",
};

const PERIODS = [
  { v: "all", label: "All time" }, { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" }, { v: "this_week", label: "This week" },
  { v: "this_month", label: "This month" },
];
const STATUSES = ["all", "pending", "replied", "declined", "cancelled"];

export default function QuotesTab() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);

  const [detail, setDetail] = useState(null);
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const [mode, setMode] = useState("view"); // view | edit | reply
  const [markupIdx, setMarkupIdx] = useState(null);
  const [reply, setReply] = useState({ price: 0, breakdown: "", admin_notes: "" });
  const [markedImages, setMarkedImages] = useState([]);
  const [edit, setEdit] = useState({});
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes", { params: { q, period, status } });
      setItems(data);
    } finally { setLoading(false); }
  }, [q, period, status]);

  useEffect(() => { load(); }, [load]);

  // open from URL param
  useEffect(() => {
    const openId = params.get("open");
    if (openId && items.length > 0) {
      const q = items.find(x => x.quote_id === openId);
      if (q) openDetail(q, "view");
      params.delete("open"); setParams(params);
    }
  // eslint-disable-next-line
  }, [items, params]);

  const openDetail = (q, m) => {
    setDetail(q);
    setMode(m);
    setReply({ price: q.price || 0, breakdown: q.breakdown || "", admin_notes: q.admin_notes || "" });
    setMarkedImages(q.marked_images || []);
    setEdit({ ...q });
    setMarkupIdx(null);
  };

  const sendReply = async () => {
    setSending(true);
    try {
      await api.post(`/admin/quotes/${detail.quote_id}/reply`, {
        price: parseFloat(reply.price || 0),
        breakdown: reply.breakdown,
        marked_images: markedImages,
        admin_notes: reply.admin_notes,
      });
      toast.success("Quote sent to customer");
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/quotes/${detail.quote_id}`, edit);
      toast.success("Quote updated"); setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancelQuote = async (id) => {
    if (!window.confirm("Cancel this quote?")) return;
    await api.post(`/admin/quotes/${id}/action`, { action: "cancel" });
    toast.success("Cancelled"); load();
  };
  const deleteQuote = async (id) => {
    if (!window.confirm("Permanently delete this quote?")) return;
    await api.delete(`/admin/quotes/${id}`);
    toast.success("Deleted"); load();
  };
  const convertToJob = async (id) => {
    await api.post(`/admin/quotes/${id}/action`, { action: "convert_to_job" });
    toast.success("Converted to job"); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Quotes</h1>
          <p className="text-sm text-muted-foreground mt-1">Customer quote requests · accepted quotes move to Jobs automatically.</p>
        </div>
        <Button onClick={() => setNewQuoteOpen(true)} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="new-quote-btn">
          <Plus className="h-4 w-4 mr-2" />New Quote
        </Button>
      </div>

      <Card className="p-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, email, phone, address…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="quotes-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="quotes-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <Card className="p-16 text-center text-muted-foreground">Loading…</Card>
      ) : items.length === 0 ? (
        <Card className="p-16 text-center text-muted-foreground">No quotes match these filters.</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left">
                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">Customer</th><th className="p-3">Address</th><th className="p-3">Setup</th>
                  <th className="p-3">Price</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(x => (
                  <tr key={x.quote_id} className="border-t border-border hover:bg-secondary/40 transition" data-testid={`quote-row-${x.quote_id}`}>
                    <td className="p-3">
                      <div className="font-medium">{x.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{x.contact_email}</div>
                      {x.product_name && <div className="text-[11px] text-eagle-gold mt-0.5 truncate max-w-[160px]">{x.product_name}</div>}
                    </td>
                    <td className="p-3 max-w-xs truncate">{x.address}{x.suburb ? `, ${x.suburb}` : ""}</td>
                    <td className="p-3">{x.num_cameras} cams · {x.property_type}</td>
                    <td className="p-3 font-semibold">{x.price ? `$${x.price.toLocaleString()}` : "—"}</td>
                    <td className="p-3"><Badge className={quoteStatusColor[x.status] + " border-0"}>{x.status}</Badge></td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(x.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(x, "view")} data-testid={`quote-view-${x.quote_id}`} title="View"><Eye className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openDetail(x, "reply")} data-testid={`quote-reply-${x.quote_id}`} title="Reply / Edit price"><Send className="h-4 w-4 text-eagle-gold" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => openDetail(x, "edit")} data-testid={`quote-edit-${x.quote_id}`} title="Edit"><Edit className="h-4 w-4" /></Button>
                        {x.status === "replied" && <Button size="sm" variant="ghost" onClick={() => convertToJob(x.quote_id)} data-testid={`quote-convert-${x.quote_id}`} title="Convert to Job"><ArrowRight className="h-4 w-4 text-emerald-600" /></Button>}
                        {x.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => cancelQuote(x.quote_id)} data-testid={`quote-cancel-${x.quote_id}`} title="Cancel"><XIcon className="h-4 w-4 text-orange-600" /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => deleteQuote(x.quote_id)} data-testid={`quote-delete-${x.quote_id}`} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-3">
                  {mode === "reply" ? "Reply to Quote" : mode === "edit" ? "Edit Quote" : "Quote Details"}
                  <Badge className={quoteStatusColor[detail.status] + " border-0"}>{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>

              {mode === "edit" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Name</Label><Input value={edit.contact_name || ""} onChange={(e) => setEdit({ ...edit, contact_name: e.target.value })} data-testid="edit-quote-name" /></div>
                  <div><Label>Email</Label><Input value={edit.contact_email || ""} onChange={(e) => setEdit({ ...edit, contact_email: e.target.value })} data-testid="edit-quote-email" /></div>
                  <div><Label>Phone</Label><Input value={edit.contact_phone || ""} onChange={(e) => setEdit({ ...edit, contact_phone: e.target.value })} /></div>
                  <div><Label>Address</Label><Input value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
                  <div><Label>Suburb</Label><Input value={edit.suburb || ""} onChange={(e) => setEdit({ ...edit, suburb: e.target.value })} /></div>
                  <div><Label># Cameras</Label><Input type="number" value={edit.num_cameras || 0} onChange={(e) => setEdit({ ...edit, num_cameras: parseInt(e.target.value || 0) })} /></div>
                  <div>
                    <Label>Wired / Wireless / Hybrid</Label>
                    <Select value={edit.installation_type || "wired"} onValueChange={(v) => setEdit({ ...edit, installation_type: v })}>
                      <SelectTrigger data-testid="edit-quote-install-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{["wired", "wireless", "hybrid"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Preferred install date</Label><Input type="date" value={edit.preferred_date || ""} onChange={(e) => setEdit({ ...edit, preferred_date: e.target.value })} data-testid="edit-quote-date" /></div>
                  <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 -mt-2">
                    <span className="text-lg font-display font-semibold" data-testid="quote-detail-name">{detail.contact_name || "Unnamed customer"}</span>
                    <span className="text-xs text-muted-foreground">· Submitted {new Date(detail.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm p-4 bg-secondary rounded-lg">
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Address</span><br />
                      <b>{detail.address}{detail.suburb ? `, ${detail.suburb}` : ""}</b>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Phone</span><br />
                      <a href={`tel:${detail.contact_phone}`} className="font-bold hover:text-eagle-gold transition-colors">{detail.contact_phone || "—"}</a>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Email</span><br />
                      <a href={`mailto:${detail.contact_email}`} className="font-bold hover:text-eagle-gold transition-colors truncate block">{detail.contact_email || "—"}</a>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Property</span><br />
                      <b className="capitalize">{detail.property_type || "—"}</b>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Setup</span><br />
                      <b>{detail.num_cameras} cams · {detail.installation_type}</b>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Preferred date</span><br />
                      <b>{detail.preferred_date ? new Date(detail.preferred_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "Flexible"}</b>
                    </div>
                  </div>
                  {(detail.status === "replied" || detail.status === "accepted") && detail.price != null && (
                    <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="quote-sent-summary">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Quote sent to customer</div>
                        <div className="font-display text-2xl font-bold">${detail.price.toLocaleString()}</div>
                      </div>
                      {detail.breakdown && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.breakdown}</p>}
                      {detail.marked_images?.length > 0 && <div className="text-xs text-muted-foreground mt-2">{detail.marked_images.length} marked-up image(s) sent</div>}
                    </div>
                  )}
                  {detail.product_name && (
                    <a
                      href={`/products/${detail.product_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg border border-eagle-gold/30 bg-eagle-gold/5 hover:bg-eagle-gold/10 transition-colors"
                      data-testid="quote-product-reference"
                    >
                      <div className="h-14 w-14 rounded-md bg-secondary overflow-hidden shrink-0">
                        {detail.product_image && <img src={detail.product_image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-eagle-gold font-semibold">Product of interest</div>
                        <div className="text-sm font-medium truncate">{detail.product_name}</div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                          {detail.product_brand && <span>{detail.product_brand}</span>}
                          {detail.product_sku && <span>· SKU: {detail.product_sku}</span>}
                          {detail.product_price != null && <span>· ${detail.product_price.toLocaleString()}</span>}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-eagle-gold shrink-0" />
                    </a>
                  )}
                  {detail.notes && (
                    <div className="p-4 rounded-lg bg-secondary/50 border-l-2 border-eagle-gold">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Customer notes</div>
                      <div className="text-sm whitespace-pre-wrap">{detail.notes}</div>
                    </div>
                  )}
                  {detail.images?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-semibold">Customer photos ({detail.images.length})</div>
                        {mode !== "reply" && (
                          <Button size="sm" onClick={() => setMode("reply")} data-testid="switch-to-reply-mode" className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">
                            <Edit className="h-3.5 w-3.5 mr-1.5" /> Mark up images & reply
                          </Button>
                        )}
                      </div>
                      {mode === "reply" && (
                        <div className="mb-3 p-3 rounded-lg border border-eagle-gold/40 bg-eagle-gold/5 text-sm">
                          <b className="text-eagle-gold">Click any image below</b> to add camera pins, draw lines, boxes, or text notes on it. Save each markup, then set the price & send.
                        </div>
                      )}
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {detail.images.map((src, i) => (
                          <button key={i} onClick={() => mode === "reply" && setMarkupIdx(i)} className={`relative aspect-square rounded-lg overflow-hidden border-2 border-transparent group ${mode === "reply" ? "hover:border-eagle-gold cursor-pointer" : "cursor-default"}`} data-testid={`quote-image-${i}`}>
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            {mode === "reply" && (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs">
                                <Edit className="h-4 w-4 mr-1" /> Mark up
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {markupIdx !== null && detail.images?.[markupIdx] && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display font-semibold">Marking image #{markupIdx + 1}</div>
                        <Button size="sm" variant="ghost" onClick={() => setMarkupIdx(null)}><XIcon className="h-4 w-4" /></Button>
                      </div>
                      <ImageMarkup src={detail.images[markupIdx]} onSave={(url) => { setMarkedImages([...markedImages, url]); toast.success("Added"); setMarkupIdx(null); }} />
                    </div>
                  )}
                  {markedImages.length > 0 && (
                    <div>
                      <div className="font-display font-semibold mb-3">Marked to send ({markedImages.length})</div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {markedImages.map((src, i) => (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => setMarkedImages(markedImages.filter((_, j) => j !== i))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"><XIcon className="h-3 w-3" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {mode === "reply" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
                      <div><Label>Price (AUD)</Label><Input type="number" value={reply.price} onChange={(e) => setReply({ ...reply, price: e.target.value })} data-testid="reply-price" /></div>
                      <div className="md:col-span-2"><Label>Breakdown / message</Label><Textarea rows={4} value={reply.breakdown} onChange={(e) => setReply({ ...reply, breakdown: e.target.value })} data-testid="reply-breakdown" /></div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                {mode === "edit" && <Button onClick={saveEdit} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="edit-quote-save">Save changes</Button>}
                {mode === "reply" && (
                  <Button onClick={sendReply} disabled={sending || !reply.price} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="reply-send-btn">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Send Quote
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <NewQuoteDialog open={newQuoteOpen} onClose={() => setNewQuoteOpen(false)} onCreated={() => load()} />
    </div>
  );
}